import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import {
  parseWorkspaceVersion,
  type WorkspaceVersionInfo
} from './workflowCards.schema';

const LATEST_DESCRIPTION = 'The last version generated for this env';

const VERSION_TYPE_COLORS: Record<
  string,
  { bg: string; border: string; outline: string }
> = {
  tag: {
    bg: 'bg-emerald-600',
    border: 'border-emerald-700',
    outline: 'outline-emerald-700'
  },
  release: {
    bg: 'bg-blue-600',
    border: 'border-blue-700',
    outline: 'outline-blue-700'
  },
  feature: {
    bg: 'bg-violet-600',
    border: 'border-violet-700',
    outline: 'outline-violet-700'
  },
  hotfix: {
    bg: 'bg-orange-600',
    border: 'border-orange-700',
    outline: 'outline-orange-700'
  },
  superfix: {
    bg: 'bg-red-600',
    border: 'border-red-700',
    outline: 'outline-red-700'
  }
};

const DEFAULT_TYPE_COLOR = {
  bg: 'bg-gray-600',
  border: 'border-gray-700',
  outline: 'outline-gray-700'
};

const getTypeColor = (type: string) =>
  VERSION_TYPE_COLORS[type.toLowerCase()] ?? DEFAULT_TYPE_COLOR;

const TypedPill = ({ version }: { version: WorkspaceVersionInfo }) => {
  const color = getTypeColor(version.type);
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-0 max-w-full items-center overflow-hidden rounded-full text-xs outline outline-1',
        color.outline
      )}
    >
      <span
        className={cn(
          'flex h-full shrink-0 items-center justify-center border-r pr-2 pl-3 text-white',
          color.bg,
          color.border
        )}
      >
        {version.type}
      </span>
      <span className="flex h-full min-w-0 items-center pr-3 pl-2">
        {/* monospace metrics render ~1px high vs the sans type label; nudge to align */}
        <span className="translate-y-px truncate font-mono">
          {version.displayName}
        </span>
      </span>
    </span>
  );
};

const LatestPill = () => (
  <span className="inline-flex h-5 min-w-0 max-w-full items-center overflow-hidden rounded-full bg-blue-100 text-blue-700 text-xs outline outline-1 outline-blue-200">
    <span className="flex h-full items-center px-3">Latest version</span>
  </span>
);

type Props = {
  version: unknown;
  className?: string;
};

export const WorkspaceVersionBadge = ({ version, className }: Props) => {
  const parsed = parseWorkspaceVersion(version);
  const isLatest = parsed === null;
  // Keep the accessible name aligned with the visible pill text (WCAG 2.5.3),
  // while the tooltip carries the longer descriptive form.
  const ariaLabel = isLatest
    ? 'Latest version'
    : `${parsed.type} ${parsed.displayName}`;
  const tooltipContent = isLatest
    ? LATEST_DESCRIPTION
    : (parsed.name ?? parsed.displayName);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={ariaLabel}
            className={cn(
              'inline-flex min-w-0 max-w-full items-center',
              className
            )}
          >
            {isLatest ? <LatestPill /> : <TypedPill version={parsed} />}
          </span>
        </TooltipTrigger>
        <TooltipContent variant="contrast">
          <span className={cn('text-xs', !isLatest && 'font-mono')}>
            {tooltipContent}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
